import type { ContactInterestUpdate } from '@sola/schema/dotcom/contacts/update.ts';
import type { GraphQLContactPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import {
  type ContactPreferenceCategory,
  ContactPreferenceChannel,
  type ContactPreferencesFormValues,
  type ContactPreferencesState,
  type OptOutChannels,
  contactPreferenceCategoryCodes,
  contactPreferenceChannels,
} from './contact-preferences-form.config.ts';

const preferenceEntries = Object.entries(contactPreferenceChannels) as [
  ContactPreferenceCategory,
  (typeof contactPreferenceChannels)[ContactPreferenceCategory],
][];

const categoryPreferencePairs = Object.entries(
  contactPreferenceCategoryCodes,
).map(([preference, code]) => [
  code,
  preference as ContactPreferenceCategory,
]) as [string, ContactPreferenceCategory][];

const categoryToPreference = new Map<string, ContactPreferenceCategory>(
  categoryPreferencePairs,
);

const preferenceToCategory = new Map<ContactPreferenceCategory, string>(
  categoryPreferencePairs.map(([category, preference]) => [
    preference,
    category,
  ]),
);

const normalizedCategoryToPreference = new Map(
  categoryPreferencePairs.map(([category, preference]) => [
    category.toLowerCase(),
    preference,
  ]),
);

export const mapContactPreferenceCodes = (
  codes: readonly string[],
): ContactPreferenceCategory[] => [
  ...new Set(
    codes.flatMap((code) => {
      const category = normalizedCategoryToPreference.get(
        code.trim().toLowerCase(),
      );
      return category ? [category] : [];
    }),
  ),
];

const isCategoryManagedForChannel = (
  category: string,
  channel: ContactPreferenceChannel,
): boolean => {
  const preference = categoryToPreference.get(category);
  if (!preference) {
    return false;
  }
  const { channels, disabledChannels, alwaysCheckedChannels } =
    contactPreferenceChannels[preference];
  if (!channels.includes(channel)) {
    return false;
  }
  if (alwaysCheckedChannels?.includes(channel)) {
    return true;
  }
  return !disabledChannels?.includes(channel);
};

const addSelection = (
  selections: ContactPreferencesState,
  preference: ContactPreferenceCategory,
  option: ContactPreferenceChannel,
) => {
  if (!contactPreferenceChannels[preference].channels.includes(option)) {
    return;
  }
  selections[preference] = [...(selections[preference] ?? []), option];
};

export const buildContactPreferencesInitialSelections = (
  preferences?: GraphQLContactPreference | null,
): ContactPreferencesState => {
  const selections = Object.fromEntries(
    preferenceEntries.map(([preference]) => [preference, []]),
  ) as ContactPreferencesState;

  for (const category of preferences?.emailInterestCategories ?? []) {
    const preference = categoryToPreference.get(category);
    if (preference) {
      addSelection(selections, preference, ContactPreferenceChannel.Email);
    }
  }

  for (const category of preferences?.textInterestCategories ?? []) {
    const preference = categoryToPreference.get(category);
    if (preference) {
      addSelection(selections, preference, ContactPreferenceChannel.Text);
    }
  }

  return selections;
};

const emptyOptOutChannels: OptOutChannels = new Set();

export const buildFormDefaultValues = (
  selections: ContactPreferencesState,
  optOutChannels: OptOutChannels = emptyOptOutChannels,
): ContactPreferencesFormValues =>
  Object.fromEntries(
    preferenceEntries.map(
      ([preference, { channels, alwaysCheckedChannels }]) => {
        const selectedOptions = selections[preference] ?? [];
        return [
          preference,
          Object.fromEntries(
            channels.map((channel) => {
              if (alwaysCheckedChannels?.includes(channel)) {
                return [channel, true];
              }
              if (optOutChannels.has(channel)) {
                return [channel, false];
              }
              return [channel, selectedOptions.includes(channel)];
            }),
          ),
        ];
      },
    ),
  ) as ContactPreferencesFormValues;

export type ReactivationFieldPath =
  `${ContactPreferenceCategory}.${ContactPreferenceChannel}`;

export interface ReactivationFieldUpdate {
  path: ReactivationFieldPath;
  value: boolean;
}

interface BuildReactivationUpdatesParams {
  currentPreferences?: GraphQLContactPreference | null;
  change: Pick<GraphQLContactPreference, 'doNotEmail' | 'doNotText'>;
}

/**
 * Restores backend selections only for channels being reactivated. Other
 * channels retain any edits already made in the open form.
 */
export const buildReactivationUpdates = ({
  currentPreferences,
  change,
}: BuildReactivationUpdatesParams): {
  fieldUpdates: ReactivationFieldUpdate[];
} => {
  const reactivatedChannels = new Set<ContactPreferenceChannel>();
  if (change.doNotEmail === false) {
    reactivatedChannels.add(ContactPreferenceChannel.Email);
  }
  if (change.doNotText === false) {
    reactivatedChannels.add(ContactPreferenceChannel.Text);
  }

  const defaultValues = buildFormDefaultValues(
    buildContactPreferencesInitialSelections(currentPreferences),
  );
  const fieldUpdates: ReactivationFieldUpdate[] = [];

  for (const [category, { channels }] of preferenceEntries) {
    for (const channel of channels) {
      if (reactivatedChannels.has(channel)) {
        fieldUpdates.push({
          path: `${category}.${channel}`,
          value: defaultValues[category]?.[channel] ?? false,
        });
      }
    }
  }

  return { fieldUpdates };
};

const selectedCategoriesForChannel = (
  values: ContactPreferencesFormValues,
  channel: ContactPreferenceChannel,
  optOutChannels: OptOutChannels,
): string[] => {
  const categories: string[] = [];
  for (const [
    preference,
    { channels, disabledChannels, alwaysCheckedChannels },
  ] of preferenceEntries) {
    if (!channels.includes(channel)) {
      continue;
    }
    const category = preferenceToCategory.get(preference);
    if (!category) {
      continue;
    }

    if (alwaysCheckedChannels?.includes(channel)) {
      continue;
    }
    if (optOutChannels.has(channel)) {
      continue;
    }
    if (disabledChannels?.includes(channel)) {
      continue;
    }
    if (values[preference]?.[channel]) {
      categories.push(category);
    }
  }
  return categories;
};

const buildChannelCategories = (
  currentCategories: string[] | null | undefined,
  values: ContactPreferencesFormValues,
  channel: ContactPreferenceChannel,
  optOutChannels: OptOutChannels,
): string[] => {
  const preserved = (currentCategories ?? []).filter(
    (category) => !isCategoryManagedForChannel(category, channel),
  );
  const selected = selectedCategoriesForChannel(
    values,
    channel,
    optOutChannels,
  );
  return [...new Set([...preserved, ...selected])];
};

export const buildPreferencePayload = (
  currentPreferences: GraphQLContactPreference | null | undefined,
  values: ContactPreferencesFormValues,
  optOutChannels: OptOutChannels = emptyOptOutChannels,
): ContactInterestUpdate['preferences'] => {
  const payload: ContactInterestUpdate['preferences'] = {};
  if (!optOutChannels.has(ContactPreferenceChannel.Email)) {
    payload.emailInterestCategories = buildChannelCategories(
      currentPreferences?.emailInterestCategories,
      values,
      ContactPreferenceChannel.Email,
      optOutChannels,
    );
  }
  if (!optOutChannels.has(ContactPreferenceChannel.Text)) {
    payload.textInterestCategories = buildChannelCategories(
      currentPreferences?.textInterestCategories,
      values,
      ContactPreferenceChannel.Text,
      optOutChannels,
    );
  }
  return payload;
};
